import { useState, useMemo } from "react";
import { Calculator, TrendingUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Card } from "@/components/ui/card";
import { fmtFJD } from "@/lib/api";

export default function ROICalculator() {
    const [landValue, setLandValue] = useState(1500000);
    const [totalProjectValue, setTotalProjectValue] = useState(8000000);
    const [equityPct, setEquityPct] = useState(22);
    const [exitMultiple, setExitMultiple] = useState(2.4);
    const [years, setYears] = useState(3);

    const result = useMemo(() => {
        const exitValue = totalProjectValue * exitMultiple;
        const landownerPayout = exitValue * (equityPct / 100);
        const upliftFjd = landownerPayout - landValue;
        const upliftPct = (upliftFjd / landValue) * 100;
        const annualised = years > 0 ? Math.pow(landownerPayout / landValue, 1 / years) - 1 : 0;
        return { exitValue, landownerPayout, upliftFjd, upliftPct, annualised: annualised * 100 };
    }, [landValue, totalProjectValue, equityPct, exitMultiple, years]);

    return (
        <div data-testid="roi-calculator-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center mb-12">
                <Calculator className="h-12 w-12 mx-auto text-coral mb-4" />
                <h1 className="font-display text-4xl sm:text-5xl font-bold mb-3">Joint Venture ROI Calculator</h1>
                <p className="font-app text-muted-foreground max-w-2xl mx-auto">Estimate what a Joint Venture could return vs. selling outright. Adjust the sliders below.</p>
            </div>

            <div className="grid lg:grid-cols-5 gap-8">
                <Card className="lg:col-span-3 p-8 space-y-6">
                    <div>
                        <Label>Land asking price (FJD)</Label>
                        <Input data-testid="roi-land-input" type="number" value={landValue} onChange={(e) => setLandValue(parseFloat(e.target.value) || 0)} className="mt-1.5" />
                    </div>
                    <div>
                        <Label>Total project value at completion (FJD)</Label>
                        <Input data-testid="roi-project-input" type="number" value={totalProjectValue} onChange={(e) => setTotalProjectValue(parseFloat(e.target.value) || 0)} className="mt-1.5" />
                    </div>
                    <div>
                        <Label>Landowner equity %: <span className="text-coral font-bold">{equityPct}%</span></Label>
                        <Slider data-testid="roi-equity-slider" value={[equityPct]} min={5} max={50} step={1} onValueChange={(v) => setEquityPct(v[0])} className="mt-3" />
                    </div>
                    <div>
                        <Label>Exit multiple (project sale ÷ project value): <span className="text-coral font-bold">{exitMultiple.toFixed(1)}×</span></Label>
                        <Slider data-testid="roi-exit-slider" value={[exitMultiple]} min={1} max={5} step={0.1} onValueChange={(v) => setExitMultiple(v[0])} className="mt-3" />
                    </div>
                    <div>
                        <Label>Hold period: <span className="text-coral font-bold">{years} years</span></Label>
                        <Slider data-testid="roi-years-slider" value={[years]} min={1} max={10} step={1} onValueChange={(v) => setYears(v[0])} className="mt-3" />
                    </div>
                </Card>

                <Card className="lg:col-span-2 p-8 bg-ocean-green text-white">
                    <TrendingUp className="h-8 w-8 text-turquoise mb-4" />
                    <h3 className="font-display text-xl font-bold mb-6">Your projected Joint Venture outcome</h3>

                    <div className="space-y-5">
                        <div>
                            <div className="text-xs uppercase tracking-wider text-turquoise font-app">Project exit value</div>
                            <div className="font-display text-2xl font-bold">{fmtFJD(result.exitValue)}</div>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-wider text-turquoise font-app">Your payout</div>
                            <div className="font-display text-3xl font-bold">{fmtFJD(result.landownerPayout)}</div>
                        </div>
                        <div className="pt-4 border-t border-white/20">
                            <div className="text-xs uppercase tracking-wider text-turquoise font-app">Uplift vs outright sale</div>
                            <div className="font-display text-2xl font-bold text-coral">+{result.upliftPct.toFixed(0)}%</div>
                            <div className="text-xs font-app text-white/70">+ {fmtFJD(result.upliftFjd)} extra</div>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-wider text-turquoise font-app">Annualised return</div>
                            <div className="font-display text-xl font-bold">{result.annualised.toFixed(1)}% / year</div>
                        </div>
                    </div>
                </Card>
            </div>

            <p className="mt-8 text-xs text-muted-foreground font-app text-center max-w-2xl mx-auto">
                Estimates only. Actual returns depend on project execution, market conditions, and the structuring of the Joint Venture. Bula Real Estate Fiji's deal team builds bespoke financial models for every approved Joint Venture.
            </p>
        </div>
    );
}
